import React, { useState } from 'react';
import { App, Button, Divider, Form, Input, InputNumber, Modal, Select, Tag, Typography } from 'antd';
import type { FormInstance } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useParseIngredientTextMutation } from '../../services/recipesApi';
import {
  useCreateInventoryItemMutation,
  useGetInventoryItemsQuery,
} from '../../services/inventoryApi';
import type { ParsedIngredientLine } from '../../types/inventory';
import { useIsModuleEnabled } from '../../hooks/useIsModuleEnabled';
import { MODULE_KEYS } from '../../constants/moduleKeys';
import { getErrorMessage } from '../../utils/errorHelpers';
import { compatibleUnits } from '../../utils/unitHelpers';

const { Text } = Typography;

export const UNIT_OPTIONS = [
  { label: 'Kilogramos (kg)', value: 'kg' },
  { label: 'Litros (L)', value: 'L' },
  { label: 'Piezas (pz)', value: 'pz' },
  { label: 'Gramos (g)', value: 'g' },
  { label: 'Mililitros (ml)', value: 'ml' },
  { label: 'Caja', value: 'caja' },
];

// Escala de la cantidad en renglones de receta, de modificador y de regla en el backend
export const RECIPE_MIN_QUANTITY = 0.0001;

// Etiqueta de un insumo en los selectores de ingredientes
export const inventoryItemLabel = (item: { name: string; unit: string; isPreparation?: boolean }) =>
  `${item.name} (${item.unit})${item.isPreparation ? ' · preparación' : ''}`;

// =====================================================
// Quick-add inventory item (inline desde la receta)
// =====================================================

interface QuickAddItemModalProps {
  open: boolean;
  initialName: string;
  onClose: () => void;
  onCreated: (item: { id: string; name: string; unit: string }) => void;
}

// Alta mínima: nombre + unidad (+ costo opcional). Sin categoría el backend
// usa "General"; stock arranca en 0 para no inventar existencias.
const QuickAddItemModal: React.FC<QuickAddItemModalProps> = ({
  open,
  initialName,
  onClose,
  onCreated,
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [createItem, { isLoading }] = useCreateInventoryItemMutation();

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const item = await createItem({
        name: values.name,
        unit: values.unit,
        costPerUnit: values.costPerUnit ?? 0,
        currentStock: 0,
        minimumStock: 0,
        isActive: true,
      }).unwrap();
      message.success(`Insumo "${item.name}" creado`);
      onCreated({ id: item.id, name: item.name, unit: item.unit });
      onClose();
    } catch (error) {
      if (error && typeof error === 'object' && 'errorFields' in error) return;
      message.error(getErrorMessage(error));
    }
  };

  return (
    <Modal
      title="Nuevo Insumo"
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      okText="Crear Insumo"
      cancelText="Cancelar"
      confirmLoading={isLoading}
      width={420}
      destroyOnClose
    >
      {/* Con nombre propio: este form vive dentro del de la receta, y sin él comparten los ids
          "name" y "unit", así que la etiqueta enfocaría el campo que queda detrás del modal */}
      <Form form={form} name="quick-add-item" layout="vertical" initialValues={{ name: initialName }}>
        <Form.Item
          name="name"
          label="Nombre"
          rules={[{ required: true, message: 'El nombre es requerido' }]}
        >
          <Input placeholder="Nombre del insumo" autoFocus />
        </Form.Item>
        <Form.Item
          name="unit"
          label="Unidad de stock"
          rules={[{ required: true, message: 'La unidad es requerida' }]}
          extra="En esta unidad se guarda el stock y el costo"
        >
          <Select placeholder="Seleccionar unidad" options={UNIT_OPTIONS} />
        </Form.Item>
        <Form.Item
          name="costPerUnit"
          label="Costo por unidad (opcional)"
          extra="Puedes dejarlo vacío y capturarlo después"
        >
          <InputNumber style={{ width: '100%' }} min={0} prefix="$" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// =====================================================
// Captura por texto (IA)
// =====================================================

interface IngredientTextBoxProps {
  onParsed: (lines: ParsedIngredientLine[]) => void;
}

// Cuadro de texto plegable. No dicta por voz a propósito: el teclado del celular ya trae
// micrófono, así que dictar sale gratis, y la API de voz del navegador falla justo en iOS.
const IngredientTextBox: React.FC<IngredientTextBoxProps> = ({ onParsed }) => {
  const { message } = App.useApp();
  const [text, setText] = useState('');
  const [parseText, { isLoading }] = useParseIngredientTextMutation();

  // allowPlanBypass:false para reflejar el gate del backend: si el módulo se apaga, el plan
  // Profesional NO lo reactiva. Sin esto quedaba un botón visible que siempre daba 403.
  const { enabled } = useIsModuleEnabled(MODULE_KEYS.RecetaTextoIA, { allowPlanBypass: false });

  if (!enabled) return null;

  const handleParse = async () => {
    const trimmed = text.trim();
    if (!trimmed) {
      message.warning('Escribe los ingredientes primero');
      return;
    }

    try {
      const result = await parseText(trimmed).unwrap();

      if (result.status !== 'ok' || result.lines.length === 0) {
        message.warning(result.note || 'No se reconocieron ingredientes en ese texto');
        return;
      }

      onParsed(result.lines);
      const nuevos = result.lines.filter((l) => l.flags.includes('insumo-nuevo')).length;
      message.success(
        nuevos > 0
          ? `${result.lines.length} ingredientes leidos, ${nuevos} sin insumo en tu inventario`
          : `${result.lines.length} ingredientes leidos`
      );
      setText('');
    } catch (error) {
      message.error(getErrorMessage(error));
    }
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <Input.TextArea
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Escribe los ingredientes como los dirias: 150 g de tortilla, 100 ml de salsa verde, 50 g de queso..."
        maxLength={2000}
      />
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 8,
        }}
      >
        <Text type="secondary" style={{ fontSize: 12, flex: '1 1 200px' }}>
          Se agregan a la lista de abajo para que los revises. Nada se guarda hasta que
          confirmes.
        </Text>
        <Button type="primary" ghost loading={isLoading} onClick={handleParse}>
          Leer ingredientes
        </Button>
      </div>
    </div>
  );
};

// Etiquetas de lo que hay que revisar en un renglon leido por IA
const FLAG_LABELS: Record<string, { text: string; color: string }> = {
  'insumo-nuevo': { text: 'Crear insumo', color: 'blue' },
  'sin-cantidad': { text: 'Falta cantidad', color: 'orange' },
  'unidad-incompatible': { text: 'Revisar unidad', color: 'orange' },
  duplicado: { text: 'Repetido', color: 'red' },
};

// =====================================================
// Editor de ingredientes (recetas y preparaciones)
// =====================================================

interface RecipeIngredientsEditorProps {
  form: FormInstance;
  isMobile: boolean;
  /** Insumo que no se puede elegir: una preparación no puede llevarse a sí misma */
  excludeItemId?: string;
  /** Lo que guarda el backend. Con un mínimo mayor, InputNumber sube sin avisar lo que leyó la IA */
  minQuantity?: number;
}

// Renglones de la lista `items` del form que lo contiene, con lectura por texto y alta
// rápida de insumos.
export const RecipeIngredientsEditor: React.FC<RecipeIngredientsEditorProps> = ({
  form,
  isMobile,
  excludeItemId,
  minQuantity = RECIPE_MIN_QUANTITY,
}) => {
  const watchedItems = Form.useWatch('items', form) as
    | Array<
        | {
            inventoryItemId?: string;
            quantity?: number;
            unit?: string;
            // Sólo presentes en renglones que vinieron de la lectura por IA
            aiFlags?: string[];
            aiRawName?: string;
          }
        | undefined
      >
    | undefined;

  // Alta rápida de insumo sin salir de la receta. El texto buscado se congela
  // al abrir el modal junto con su renglón: `ingredientSearch` es compartido
  // por todos los renglones y rc-select no lo limpia al seleccionar, así que
  // leerlo después prellenaría el modal con el nombre de otro renglón.
  const [quickAdd, setQuickAdd] = useState<{ row: number; search: string } | null>(null);
  const [ingredientSearch, setIngredientSearch] = useState('');

  // Los recién creados se mezclan a las opciones hasta que el refetch los
  // traiga; es una lista porque pueden crearse varios antes de que llegue
  // (con uno solo, el renglón anterior mostraría el GUID crudo y perdería la
  // restricción de unidades compatibles).
  const [justCreated, setJustCreated] = useState<Array<{ id: string; name: string; unit: string }>>(
    []
  );

  const { data: inventoryItems, isLoading: loadingItems } = useGetInventoryItemsQuery();

  const selectableItems = React.useMemo<
    Array<{ id: string; name: string; unit: string; isPreparation?: boolean }>
  >(() => {
    const base = (inventoryItems ?? []).filter((i) => i.id !== excludeItemId);
    const pending = justCreated.filter((c) => !base.some((i) => i.id === c.id));
    return pending.length > 0 ? [...base, ...pending] : base;
  }, [inventoryItems, justCreated, excludeItemId]);

  return (
    <>
      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        Ingredientes
      </Text>

      <IngredientTextBox
        onParsed={(parsed) => {
          // Los renglones leidos se AGREGAN a lo que ya haya, respetando lo que el usuario
          // capturo a mano. Los vacios iniciales se descartan para no dejar huecos.
          const current = (form.getFieldValue('items') || []) as Array<{
            inventoryItemId?: string;
            quantity?: number;
            unit?: string;
          }>;
          const kept = current.filter((row) => row?.inventoryItemId || row?.quantity);

          const added = parsed.map((line) => ({
            // Leer su propio nombre al editar una preparación no la vuelve su ingrediente:
            // queda sin insumo, con su aviso, en vez de un valor que el selector no ofrece.
            inventoryItemId: line.inventoryItemId === excludeItemId ? undefined : line.inventoryItemId,
            quantity: line.quantity,
            unit: line.unit,
            // Se guardan en el form para poder pintar los avisos por renglon; el submit
            // solo manda inventoryItemId/quantity/unit.
            aiFlags: line.flags,
            aiRawName: line.rawName,
          }));

          form.setFieldValue('items', [...kept, ...added]);
        }}
      />

      <Form.List name="items">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => {
              // La unidad se restringe a las compatibles con la unidad de
              // stock del insumo (g↔kg, ml↔L); el backend rechaza el resto.
              const row = watchedItems?.[name];
              const stockUnit = selectableItems.find(
                (item) => item.id === row?.inventoryItemId
              )?.unit;
              const aiRawName = row?.aiRawName;

              // Los avisos se RECALCULAN contra el estado actual del renglón en vez de
              // pintar los que trajo la lectura: si no, "Falta cantidad" seguía ahí después
              // de escribirla, y un aviso que no se apaga deja de leerse — tapando a los que
              // sí siguen importando.
              const liveFlags: string[] = [];
              if (aiRawName) {
                if (!row?.inventoryItemId) liveFlags.push('insumo-nuevo');
                if (row?.quantity === undefined || row?.quantity === null) {
                  liveFlags.push('sin-cantidad');
                }
                const unitOk =
                  !!row?.unit &&
                  (!stockUnit ||
                    compatibleUnits(stockUnit).some(
                      (u) => u.toLowerCase() === String(row.unit).toLowerCase()
                    ));
                if (!unitOk) liveFlags.push('unidad-incompatible');
              }
              // El duplicado se avisa en TODOS los renglones, vengan de donde vengan: el
              // backend ahora rechaza la receta, así que verlo antes de guardar evita un
              // error al confirmar.
              if (
                row?.inventoryItemId &&
                (watchedItems ?? []).filter((r) => r?.inventoryItemId === row.inventoryItemId)
                  .length > 1
              ) {
                liveFlags.push('duplicado');
              }
              const unitOptions = stockUnit
                ? compatibleUnits(stockUnit).map(
                    (u) =>
                      UNIT_OPTIONS.find(
                        (o) => o.value.toLowerCase() === u.toLowerCase()
                      ) ?? { label: u, value: u }
                  )
                : UNIT_OPTIONS;

              return (
              <div
                key={key}
                style={{
                  display: 'flex',
                  gap: 8,
                  marginBottom: 8,
                  flexWrap: isMobile ? 'wrap' : 'nowrap',
                  alignItems: 'flex-start',
                  padding: isMobile ? 8 : 0,
                  background: isMobile ? 'var(--bg-secondary)' : undefined,
                  borderRadius: isMobile ? 8 : 0,
                }}
              >
                <Form.Item
                  {...restField}
                  name={[name, 'inventoryItemId']}
                  rules={[{ required: true, message: 'Requerido' }]}
                  style={{ flex: isMobile ? '1 1 100%' : '1 1 40%', marginBottom: isMobile ? 4 : 0 }}
                >
                  <Select
                    showSearch
                    placeholder="Ingrediente"
                    loading={loadingItems}
                    optionFilterProp="label"
                    onSearch={setIngredientSearch}
                    // rc-select no limpia el texto buscado al seleccionar ni
                    // al cerrar: si no se limpia aquí, el siguiente renglón
                    // arrastra el nombre tecleado en este
                    onOpenChange={(isOpen) => {
                      if (!isOpen) setIngredientSearch('');
                    }}
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        <Divider style={{ margin: '4px 0' }} />
                        <Button
                          type="text"
                          icon={<PlusOutlined />}
                          block
                          style={{ textAlign: 'left' }}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() =>
                            setQuickAdd({
                              row: name,
                              // Si el renglón vino de la lectura por IA, el nombre que
                              // propuso el cocinero gana sobre lo que se haya tecleado
                              search: ingredientSearch || aiRawName || '',
                            })
                          }
                        >
                          {ingredientSearch || aiRawName
                            ? `Crear insumo "${ingredientSearch || aiRawName}"`
                            : 'Crear insumo nuevo'}
                        </Button>
                      </>
                    )}
                    onChange={(value: string) => {
                      setIngredientSearch('');
                      const newStockUnit = selectableItems.find((item) => item.id === value)?.unit;
                      if (!newStockUnit) return;
                      // Si la unidad ya capturada sigue siendo compatible se
                      // respeta (250 g siguen siendo 250 g al cambiar de
                      // harina); si no, se resetea unidad Y cantidad para no
                      // heredar una magnitud con otro significado (250 g →
                      // 250 kg sería un error de 1000x creado por la UI).
                      const currentUnit = form.getFieldValue(['items', name, 'unit']);
                      const stillCompatible = currentUnit &&
                        compatibleUnits(newStockUnit).some(
                          (u) => u.toLowerCase() === String(currentUnit).toLowerCase()
                        );
                      if (!stillCompatible) {
                        form.setFieldValue(['items', name, 'unit'], newStockUnit);
                        form.setFieldValue(['items', name, 'quantity'], undefined);
                      }
                    }}
                    options={selectableItems.map((item) => ({
                      value: item.id,
                      label: inventoryItemLabel(item),
                    }))}
                  />
                </Form.Item>

                <Form.Item
                  {...restField}
                  name={[name, 'quantity']}
                  rules={[{ required: true, message: 'Requerido' }]}
                  style={{ flex: isMobile ? '1 1 40%' : '0 0 120px', marginBottom: isMobile ? 4 : 0 }}
                >
                  <InputNumber
                    placeholder="Cantidad"
                    min={minQuantity}
                    step={0.1}
                    style={{ width: '100%' }}
                  />
                </Form.Item>

                <Form.Item
                  {...restField}
                  name={[name, 'unit']}
                  rules={[{ required: true, message: 'Requerido' }]}
                  style={{ flex: isMobile ? '1 1 40%' : '0 0 130px', marginBottom: isMobile ? 4 : 0 }}
                >
                  <Select placeholder="Unidad" options={unitOptions} />
                </Form.Item>

                <Button
                  type="text"
                  danger
                  icon={<MinusCircleOutlined />}
                  onClick={() => remove(name)}
                  style={{ marginTop: isMobile ? 0 : 4 }}
                />

                {liveFlags.length > 0 && (
                  <div style={{ flex: '1 1 100%', marginTop: -2, marginBottom: 4 }}>
                    {aiRawName && (
                      <Text type="secondary" style={{ fontSize: 12, marginRight: 8 }}>
                        Leido: "{aiRawName}"
                      </Text>
                    )}
                    {liveFlags.map((flag) => {
                      const label = FLAG_LABELS[flag];
                      return label ? (
                        <Tag key={flag} color={label.color} style={{ fontSize: 11 }}>
                          {label.text}
                        </Tag>
                      ) : null;
                    })}
                  </div>
                )}
              </div>
              );
            })}

            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
              Agregar Ingrediente
            </Button>
          </>
        )}
      </Form.List>

    <QuickAddItemModal
      open={quickAdd !== null}
      initialName={quickAdd?.search ?? ''}
      onClose={() => setQuickAdd(null)}
      onCreated={(item) => {
        if (!quickAdd) return;
        setJustCreated((prev) => [...prev, item]);
        form.setFieldValue(['items', quickAdd.row, 'inventoryItemId'], item.id);

        // La cantidad se CONSERVA si la unidad que ya tenía el renglón sigue siendo
        // compatible con la del insumo recién creado. Borrarla siempre tiraba justo el
        // número que la lectura acababa de sacar del texto ("30 g de chile de árbol"),
        // que es el flujo entero de esta feature. Sólo se limpia cuando la unidad deja de
        // significar lo mismo, para no convertir 250 g en 250 kg.
        const currentUnit = form.getFieldValue(['items', quickAdd.row, 'unit']);
        const stillCompatible =
          currentUnit &&
          compatibleUnits(item.unit).some(
            (u) => u.toLowerCase() === String(currentUnit).toLowerCase()
          );

        if (!stillCompatible) {
          form.setFieldValue(['items', quickAdd.row, 'unit'], item.unit);
          form.setFieldValue(['items', quickAdd.row, 'quantity'], undefined);
        }

        setIngredientSearch('');
      }}
    />
    </>
  );
};
